import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import DrawerListItem from './DrawerListItem';
import DrawerNestedListItem from './DrawerNestedListItem';
import { useSelector } from 'react-redux';
import { getCurrentSecurityGroup } from '../../features/security/selectors';
import {
  DOCUMENT_TYPES_BIDDING,
  DOCUMENT_TYPES_CLOSE_OUT,
  DOCUMENT_TYPES_CONSTRUCTION,
  DOCUMENT_TYPES_DESIGN,
  documentTypeLookup,
} from '../../scripts/utils';
import { getDocumentsAssignedToMeState, getProjectState } from '../../features/project/selectors';
import { getUserState } from '../../features/user/selectors';
import { useAppUtilsContext } from '../AppUtilsContext';

interface DrawerNestedListProps {
  title: string;
  listIndex: number;
  handleDrawer: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => void;
  handleNestedLists: () => void;
  selected: number;
  icon: JSX.Element;
  drawerOpen: boolean;
  menuOpen: boolean;
  subtitles: string[];
  biddingPortal?: boolean;
  projectInfoType?: string;
  disabled?: boolean;
  handleTypeSelect?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, type: string) => void;
  initiateSelectedItem?: (setIndex: (index: number) => void, area: string) => void;
}

const getDocumentTypesFromTitle = (title: string) => {
  switch (title) {
    case 'Design':
      return DOCUMENT_TYPES_DESIGN;

    case 'Bidding':
      return DOCUMENT_TYPES_BIDDING;

    case 'Construction':
      return DOCUMENT_TYPES_CONSTRUCTION;

    case 'Close Out':
      return DOCUMENT_TYPES_CLOSE_OUT;

    default:
      return [];
  }
};

export default function DrawerNestedList(props: DrawerNestedListProps) {
  const {
    title,
    listIndex,
    handleDrawer,
    handleNestedLists,
    selected,
    icon,
    drawerOpen,
    menuOpen,
    subtitles,
    biddingPortal,
    projectInfoType,
    disabled = false,
    handleTypeSelect,
    initiateSelectedItem,
  } = props;

  const history = useHistory();
  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);
  const security = useSelector(getCurrentSecurityGroup);
  const documentsAssignedToMeCount = useSelector(getDocumentsAssignedToMeState);

  const {
    documentTemplates: { documentTemplateNameToId },
  } = useAppUtilsContext();

  const [selectedIndex, setSelectedIndex] = useState(-1);

  const handleNestedItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
  ) => {
    setSelectedIndex(index);
  };

  const handleBidModuleClick = () => {
    history.push(`/bidding/${project?.bidSetupId || ''}`);
  };

  const handleBidMgmtClick = () => {
    history.push(`/main/projects/${project!.id}/bid-mgmt`);
  };

  const handleConstMgmtClick = () => {
    history.push(`/main/projects/${project!.id}/const-mgmt`);
  };

  const handlePubCenterClick = () => {
    history.push(`/main/projects/${project!.id}/pub-center`);
  };

  const handleDriveClick = () => {
    history.push(`/main/projects/${project!.id}/design`);
  };

  const handleManageProjectClick = () => {
    history.push(`/main/projects/${project!.id}/manage`);
  };

  const handleCorrespondenceLogClick = () => {
    history.push(`/main/projects/${project!.id}/correspondence-log`);
  };

  const handleMyNotificationsClick = () => {
    history.push(`/main/projects/${project!.id}/my-notifications`);
  };

  const handleProjectLifecycleClick = () => {
    history.push(`/main/projects/${project!.id}/project-lifecycle`);
  };

  const handleProjectCloseoutClick = () => {
    history.push(`/main/projects/${project!.id}/project-closeout`);
  };

  const handleProjectProgressReportsClick = () => {
    history.push(`/main/projects/${project!.id}/project-progress-reports`);
  };

  const handleQualityControlFormsClick = () => {
    history.push(`/main/projects/${project!.id}/project-quality-control-forms`);
  };

  const handleA3Click = () => {
    history.push(`/main/projects/${project!.id}/a3`);
  };

  const getDisabled = (subtitle: string) => {
    switch (subtitle) {
      case 'CENTERLINE BidConnect':
        return false;

      default:
        return disabled;
    }
  };

  const getAssignedDocumentCount = () => {
    const documentTemplateIds = getDocumentTypesFromTitle(title).map(
      (d) => documentTemplateNameToId[d],
    );
    return documentsAssignedToMeCount
      .filter((d) => documentTemplateIds.includes(d.document_template_id))
      .reduce((prev, curr) => prev + Number(curr.count), 0);
  };

  useEffect(() => {
    if (initiateSelectedItem !== undefined) initiateSelectedItem(setSelectedIndex, 'items');
  });

  const getPageChange = (subtitle: string) => {
    switch (subtitle) {
      case 'Bid Setup':
        return handleBidMgmtClick;

      case 'Bid Management':
        return handleBidMgmtClick;

      case 'Construction Setup':
        return handleConstMgmtClick;

      case 'Construction Management':
        return handleConstMgmtClick;

      case 'CENTERLINE BidConnect':
        return handleBidModuleClick;

      case 'Publishing Center':
        return handlePubCenterClick;

      case 'CENTERLINE Drive':
        return handleDriveClick;

      case 'Manage Project':
        return handleManageProjectClick;

      case 'Correspondence Log':
        return handleCorrespondenceLogClick;

      case 'My Notification Inbox':
        return handleMyNotificationsClick;

      case 'Project Lifecycle':
        return handleProjectLifecycleClick;

      case 'Project Closeout':
        return handleProjectCloseoutClick;

      case 'Project Progress Reports':
        return handleProjectProgressReportsClick;

      case 'Quality Control Forms':
        return handleQualityControlFormsClick;

      case 'A3 Summary':
        return handleA3Click;

      default:
        return undefined;
    }
  };

  const userHasPermission = (subtitle: string) => {
    if (user.isSiteAdmin || user.adminOfSubscriberId) return true;
    if (subtitle === 'CENTERLINE Drive') return true;
    if (subtitle === 'Manage Project') return true;
    if (subtitle === 'CENTERLINE BidConnect') return true;
    if (biddingPortal) return true;
    if (subtitle === 'Bid Setup' || subtitle === 'Bid Management') return true;
    if (subtitle === 'Construction Management' || subtitle === 'Construction Setup') return true;
    if (subtitle === 'Publishing Center') return true;
    if (subtitle === 'Correspondence Log') return true;
    if (subtitle === 'My Notification Inbox') return true;
    if (
      [
        'Project Lifecycle',
        'Project Closeout',
        'Project Progress Reports',
        'Quality Control Forms',
        'A3 Summary',
      ].includes(subtitle)
    )
      return true;

    const docType = subtitle in documentTypeLookup ? documentTypeLookup[subtitle] : subtitle;
    const docPermission = security?.securityGroupDocumentTemplateList!.find(
      (group) => group.documentTemplateId === documentTemplateNameToId[docType],
    )?.securityPermissionLevel;
    return !!(security && docPermission !== null && docPermission !== undefined);
  };

  const subtitlesFiltered = subtitles.filter((s) => userHasPermission(s));

  const items = subtitlesFiltered.map((subtitle, index) => {
    return (
      <div key={subtitle}>
        {index === 0 ||
        subtitle === 'Bid Drawings' ||
        subtitle === 'Drawings' ||
        subtitle === 'CA Task Manager' ||
        subtitle === 'Change Orders' ||
        subtitle === 'Field Reports' ||
        subtitle === 'O&M Data' ||
        subtitle === 'Punch List' ||
        subtitle === 'Cert. of Substantial Completion' ? (
          <Divider variant="middle" light flexItem style={{ margin: '10px' }} />
        ) : undefined}
        <>
          <DrawerNestedListItem
            key={`${subtitle}`}
            title={subtitle}
            listIndex={index}
            onClick={handleNestedItemClick}
            selected={selectedIndex}
            handleTypeSelectBid={handleTypeSelect}
            biddingPortal={biddingPortal}
            projectInfoType={projectInfoType}
            disabled={getDisabled(subtitle)}
            handlePageChange={getPageChange(subtitle)}
          />
          {subtitle === 'Informational Items' ? (
            <Divider variant="middle" light flexItem style={{ margin: '10px' }} />
          ) : undefined}
        </>
      </div>
    );
  });

  return (
    <>
      {items.filter((item) => item).length > 0 && (
        <div>
          <DrawerListItem
            title={title}
            listIndex={listIndex}
            onClick={(event) => {
              if (!biddingPortal) handleDrawer(event, listIndex);
              handleNestedLists();
            }}
            selectedIndex={selected}
            icon={icon}
            drawerOpen={drawerOpen}
            menuOpen={menuOpen}
            selectedFunc={
              projectInfoType
                ? () => {
                    return subtitlesFiltered.includes(projectInfoType!);
                  }
                : undefined
            }
            assignedDocumentCount={getAssignedDocumentCount()}
          />

          <Collapse
            in={drawerOpen && menuOpen}
            timeout="auto"
            unmountOnExit
            style={{ backgroundColor: '#001A4D' }}
          >
            <List component="div" style={{ paddingBottom: 8, paddingTop: 0 }}>
              {items}
            </List>
          </Collapse>
        </div>
      )}
    </>
  );
}
